import * as React from 'react'
import {Box} from '@mui/material'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import {GAP} from '../../../../styles/foundation/spacing'
import {filterRequests, requestCounts} from '../requests/logic'
import StepRail from '../components/StepRail'
import RqKpiStrip, {LABEL as STRIP_LABEL, requestCards} from '../components/RqKpiStrip'
import InitiatePage from './InitiatePage'
import RequestsSection, {STATUS_LABEL} from '../RequestsSection'

/**
 * One glyph per state, module-wide. Every one of these is already the icon this
 * module paints for that state elsewhere — AcUnitOutlined is the freeze section's
 * own icon (sections.js:47) and what IniRaiseDrawer heads the raise form with,
 * and the three status glyphs are the ones requests/RqStats.js:19-23 puts on the
 * approved / review / rejected tiles. A tab and a tile describing the same state
 * must not use two different pictures of it.
 */
const TAB_ICON = {
  initiate: AcUnitOutlinedIcon,
  review: HourglassEmptyIcon,
  approved: CheckCircleOutlineIcon,
  rejected: CancelOutlinedIcon,
}

// The three status tabs, in the order a request travels. 'initiate' is prepended
// rather than listed here because it is the only one with no count.
const STATUS_TABS = ['review', 'approved', 'rejected']

/**
 * Weight Freeze (v2) — Initiate freeze: the one dashboard the rail mounts for
 * "raise a freeze and check its status", per the verbatim requirement this
 * task was given.
 *
 * A KPI strip, then ONE tab row of four, then the selected view.
 *
 * The strip is the five request-status figures, lifted out of RequestsSection
 * to sit ABOVE the tab row so they show on all four views instead of only on
 * the three status ones — the merchant asked for the Weight Discrepancy header,
 * where the strip is likewise tab-independent context for the whole page rather
 * than part of any one tab. RequestsSection still renders them when nobody is
 * driving its axis; see its `tab` prop. Its numbers come from `requestCounts`,
 * the UNFILTERED collection, and must keep doing so: the search box that could
 * otherwise move them is in a card further down this same page.
 *
 * ONE tab row, four tabs, owned here. Raising a freeze and the three states a
 * raised one can be in are four answers to the same question — what am I
 * looking at — so they sit at one level, ordered the way a request actually
 * travels: initiate, under review, approved, rejected. The last three are
 * `RequestsSection`'s own axis, driven from up here through its optional `tab`
 * prop, which is also what suppresses the second row that component renders
 * when nobody is driving it. Their labels come from that component's exported
 * `STATUS_LABEL`, so these strings exist once.
 *
 * The row is `StepRail` at its `comfortable` density, NOT a local strip: this
 * module already had one icon-tile treatment and a second implementation of it
 * would be free to drift from the first. What the row needed from that
 * component was a per-item count, which is now an optional field on a section —
 * see StepRail's DENSITY block for why the size differs from the section rails'.
 *
 * `InitiatePage` and `RequestsSection` used to be two separate rail
 * destinations, each already wired to send the merchant to the other — the
 * only way to get from raising a request to seeing it, or from a rejected
 * request back to a fresh one. `handleNavigate` below is what keeps those
 * cross-links alive: it catches the two legacy ids naming this dashboard's own
 * halves ('requests' and 'initiate') and turns them into a local tab switch
 * instead of a rail jump. 'requests' lands on Under review, because every
 * caller of it is a merchant who has just raised or resubmitted something and
 * that is the state such a request is in. Every other id a child page can send
 * ('dws', 'sop', 'catalogue', 'packing') still has to leave this
 * dashboard, so those fall through to the real navigate handler this component
 * received from the rail.
 *
 * Only the selected tab's content is mounted — Initiate and the three status
 * lists together run to roughly 450 lines of table on a single scroll, which
 * is what the local switch exists to avoid. `initialView` ('initiate' |
 * 'status') lets the rail deep-link past Initiate; 'status' resolves to Under
 * review, for the same reason 'requests' does.
 */
export default function FreezeDashboard(props) {
  const {requests, onNavigate, initialView = 'initiate'} = props
  const [tab, setTab] = React.useState(initialView === 'status' ? 'review' : 'initiate')

  const handleNavigate = React.useCallback((id) => {
    if (id === 'requests') { setTab('review'); return }
    if (id === 'initiate') { setTab('initiate'); return }
    onNavigate(id)
  }, [onNavigate])

  // Counted over the WHOLE collection, deliberately: this row now sits ABOVE
  // RequestsSection's search/filter card, and a tab up here that renumbered
  // itself as the merchant typed into a card further down the page would be
  // describing something other than the dashboard it heads. `filterRequests`
  // with no filter is that unfiltered partition, so these three numbers still
  // come from the same function the rows inside each tab do — not from the
  // stat cards' `requestCounts`, which answers a different question again.
  const TABS = React.useMemo(() => {
    const all = filterRequests(requests)
    return [
      {id: 'initiate', label: 'Initiate weight freeze', icon: TAB_ICON.initiate},
      ...STATUS_TABS.map((id) => ({
        id, label: STATUS_LABEL[id], count: all[id].length, icon: TAB_ICON[id],
      })),
    ]
  }, [requests])

  const counts = React.useMemo(() => requestCounts(requests), [requests])

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* Strip and row are one block: the strip is the header the row belongs
          to, so they sit closer to each other (GAP.md, matching the mb: 1.5
          disputes-v2/KpiStrip.js:70 puts between them) than the row does to the
          content below it. */}
      <Box sx={{display: 'grid', gap: GAP.md}}>
        <RqKpiStrip cards={requestCards(counts)} label={STRIP_LABEL} onSelect={setTab} />
        <StepRail
          sections={TABS}
          active={tab}
          onChange={setTab}
          density='comfortable'
          label='Weight freeze views'
          // The reference header (disputes-v2) keeps its tab bar visible and
          // horizontally scrollable at every width rather than collapsing to
          // a select. Four tabs scroll fine on a phone — StepRail's own
          // select fallback exists for the module rail's seven, not this row.
          disableSelectFallback
        />
      </Box>

      {tab === 'initiate'
        ? <InitiatePage {...props} onNavigate={handleNavigate} />
        : <RequestsSection {...props} onNavigate={handleNavigate} tab={tab} />}
    </Box>
  )
}
